import { useTranslation } from "react-i18next";
import type { ColDef } from "ag-grid-community";
import { parseStringToDate } from "../../tools/converters/DateConverter.ts";
import { convertColorToStatus } from "../../tools/converters/StatusColorConverter.ts";
import { Report } from "../../generated/fresh-guard-api";
import type { AllKeysUndefinedCapable } from "./Report.type.ts";

export const useReportHelper = () => {
  const { t } = useTranslation();
  const marketComponentOrderMap = {
    number: "orderNumber",
    created: "orderCreated",
    shortDescription: "orderShortDescription",
    description: "orderDescription",
    articleType: "orderMarketComponentArticleTypeName",
    type: "orderMarketComponentTypeName",
    marketName: "orderMarketName",
    lastValidation: "orderMarketComponentHistoriesCreated",
  } as const;

  const marketComponentHistoryOrderMap = {
    market: "orderMarketComponentMarketName",
    marketComponentName: "orderMarketComponentShortDescription",
    marketComponentNumber: "orderMarketComponentNumber",
    date: "orderCreated",
    username: "orderUserUsername",
    status: "orderStatus",
    criticalDate: "orderCriticalDate",
  } as const;

  const userOrderMap = {
    username: "orderUsername",
    firstname: "orderFirstname",
    lastname: "orderLastname",
    deactivated: "orderDeactivated",
    deactivatedBy: "orderDeactivatedByUsername",
    checkCount: "orderCheckCount",
  } as const;

  const isReport = (type: string | null): Report | undefined => {
    return Object.values(Report).find(
      (value): value is Report => value === type,
    );
  };

  const generateTitle = (type: string): string => {
    switch (type) {
      case Report.MarketComponentHistory:
        return t("ui.reportMarketComponentHistory");
      case Report.MarketComponent306090Verification:
        return t("ui.reportMarketComponent_30_60_90_Verification");
      case Report.MarketComponentWithoutCriticalBbd:
        return t("ui.reportMarketComponentWithoutCriticalBBD");
      case Report.MarketComponentWhereShortDescriptionNotEqualsNumber:
        return t(
          "ui.reportMarketComponentWhereShortDescriptionNotEqualsNumber",
        );
      case Report.User:
        return t("ui.reportUser");
    }

    return "";
  };

  const generateColumnDefByType = (type: string): ColDef[] => {
    switch (type) {
      case Report.MarketComponentHistory:
        return [
          {
            field: "market",
            headerName: t("table.reportMarketComponentHistory.market"),
          },
          {
            field: "marketComponentName",
            headerName: t(
              "table.reportMarketComponentHistory.marketComponentName",
            ),
          },
          {
            field: "marketComponentNumber",
            headerName: t(
              "table.reportMarketComponentHistory.marketComponentNumber",
            ),
          },
          {
            field: "date",
            headerName: t("table.reportMarketComponentHistory.date"),
            valueFormatter: (params) => {
              if (!params.value) return "";

              return parseStringToDate(params.value, t) ?? "";
            },
          },
          {
            field: "username",
            headerName: t("table.reportMarketComponentHistory.username"),
          },
          {
            field: "status",
            headerName: t("table.reportMarketComponentHistory.status"),
            valueFormatter: (params) => {
              if (!params.value) return "";

              return convertColorToStatus(params.value, t) ?? "";
            },
          },
          {
            field: "criticalDate",
            headerName: t("table.reportMarketComponentHistory.criticalDate"),
            valueFormatter: (params) => {
              if (!params.value) return "";

              return parseStringToDate(params.value, t, false) ?? "";
            },
          },
        ];
      case Report.MarketComponent306090Verification:
      case Report.MarketComponentWithoutCriticalBbd:
      case Report.MarketComponentWhereShortDescriptionNotEqualsNumber:
        return [
          {
            field: "shortDescription",
            headerName: t("table.reportMarketComponent.shortDescription"),
          },
          {
            field: "description",
            headerName: t("table.reportMarketComponent.description"),
          },
          {
            field: "articleType",
            headerName: t("table.reportMarketComponent.articleType"),
          },
          {
            field: "type",
            headerName: t("table.reportMarketComponent.type"),
          },
          {
            field: "marketName",
            headerName: t("table.reportMarketComponent.marketName"),
          },
          {
            field: "number",
            headerName: t("table.reportMarketComponent.number"),
          },
          {
            field: "lastValidation",
            headerName: t("table.reportMarketComponent.lastValidation"),
            valueFormatter: (params) => {
              if (!params.value) return "";

              return parseStringToDate(params.value, t) ?? "";
            },
          },
          {
            field: "created",
            headerName: t("table.reportMarketComponent.created"),
            valueFormatter: (params) => {
              if (!params.value) return "";

              return parseStringToDate(params.value, t) ?? "";
            },
          },
        ];
      case Report.User:
        return [
          {
            field: "username",
            headerName: t("table.reportUser.username"),
          },
          {
            field: "firstname",
            headerName: t("table.reportUser.firstname"),
          },
          {
            field: "lastname",
            headerName: t("table.reportUser.lastname"),
          },
          {
            field: "checkCount",
            headerName: t("table.reportUser.checkCount"),
          },
          {
            field: "deactivated",
            headerName: t("table.reportUser.deactivated"),
            valueFormatter: (params) => {
              if (!params.value?.date) return "";

              return parseStringToDate(params.value.date, t) ?? "";
            },
          },
          {
            field: "deactivatedBy",
            headerName: t("table.reportUser.deactivatedBy"),
          },
        ];
    }

    return [];
  };

  const buildOrderDTO = <T extends Record<string, "asc" | "desc" | undefined>>(
    sortedBy: { colId: string; sort: "asc" | "desc" }[],
    map: Record<string, keyof T>,
  ): AllKeysUndefinedCapable<T> => {
    const dto = {} as AllKeysUndefinedCapable<T>;

    for (const key of Object.values(map)) {
      dto[key] = undefined;
    }

    for (const { colId, sort } of sortedBy) {
      const targetKey = map[colId];
      if (targetKey) dto[targetKey] = sort;
    }

    return dto;
  };

  return {
    isReport,
    generateTitle,
    generateColumnDefByType,
    buildOrderDTO,
    marketComponentOrderMap,
    marketComponentHistoryOrderMap,
    userOrderMap,
  };
};
